import type { ReactNode, RefObject } from 'react';
import { formatCurrencyWithCommas } from '@/utils/formatNumber';
import { RightSectionWrapper } from './RightSectionWrapper';

export function EmployerContributionPremium({
    rowRef,
    rightSection,
    rightRef,
    isRightWrapped,
    isFamilyPlan,
    premiumNumber,
    employerContributionAmount,
    employerPay,
    employeePay,
    available,
    employeeTaxLabel,
}: {
    rowRef: RefObject<HTMLDivElement | null>;
    rightSection?: ReactNode;
    rightRef: RefObject<HTMLDivElement | null>;
    isRightWrapped: boolean;
    isFamilyPlan: boolean;
    premiumNumber: number;
    employerContributionAmount: number;
    employerPay: number;
    employeePay: number;
    available: number;
    employeeTaxLabel: string;
}) {
    return (
        <div className="w-full rounded-2xl">
            <div
                ref={rowRef}
                className="flex flex-col items-stretch justify-start gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:justify-start lg:gap-4"
            >
                <div className="flex w-full min-w-0 flex-1 flex-row flex-nowrap items-start justify-start gap-2 sm:w-auto">
                    {/* Premium */}
                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                        <div className="relative flex h-[78px] md:h-[78px] lg:h-[38px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white">
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    padding: 1,
                                    borderRadius: 8,
                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    pointerEvents: 'none',
                                }}
                            />
                            <span className="relative w-full min-w-0 truncate text-center text-base font-medium text-primary-500 tabular-nums">
                                {formatCurrencyWithCommas(premiumNumber)}
                            </span>
                        </div>
                        <div className="mt-1 w-full text-sm font-normal text-center leading-tight text-gray-900">
                            {isFamilyPlan ? 'Family Monthly Premium' : 'Monthly Premium'}
                        </div>
                    </div>

                    <div className="h-[78px] md:h-[78px] lg:h-[43px] flex items-center">
                        <span className="text-base font-medium text-gray-700">-</span>
                    </div>

                    {/* Employer */}
                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                        <div className="relative flex h-[78px] md:h-[78px] lg:h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-brand-100">
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    padding: 1,
                                    borderRadius: 8,
                                    background: 'transparent',
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    pointerEvents: 'none',
                                }}
                            />
                            <div className="relative flex w-full min-w-0 flex-col items-center justify-center gap-0.5 leading-none lg:flex-row lg:gap-2">
                                <span className="w-full min-w-0 truncate text-center text-base font-normal text-gray-500 lg:w-auto">
                                    Employer:
                                </span>
                                <span className="w-full min-w-0 truncate text-center text-base font-medium text-gray-900 tabular-nums lg:w-auto">
                                    {formatCurrencyWithCommas(employerPay)}
                                </span>
                            </div>
                        </div>
                        <div className="mt-1 w-full text-sm font-normal text-center leading-tight text-brand-400 whitespace-normal wrap-break-word">
                            {`${formatCurrencyWithCommas(available)} of ${formatCurrencyWithCommas(employerContributionAmount)} Available`}
                        </div>
                    </div>

                    <div className="h-[78px] md:h-[78px] lg:h-[43px] flex items-center">
                        <span className="text-base font-medium text-gray-700">=</span>
                    </div>

                    {/* Employee */}
                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                        <div className="relative flex h-[78px] md:h-[78px] lg:h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white border border-brand-100">
                            <div className="relative flex w-full min-w-0 flex-col items-center justify-center gap-0.5 leading-none lg:flex-row lg:gap-2">
                                <span className="w-full min-w-0 truncate text-center text-base font-normal text-gray-500 lg:w-auto">
                                    Employee:
                                </span>
                                <span className="w-full min-w-0 truncate text-center text-base font-medium text-gray-900 tabular-nums lg:w-auto">
                                    {formatCurrencyWithCommas(employeePay)}
                                </span>
                            </div>
                        </div>
                        <div className="mt-1 w-full text-sm font-normal text-center leading-tight text-gray-900 whitespace-normal wrap-break-word">
                            {employeeTaxLabel}
                        </div>
                    </div>
                </div>

                <RightSectionWrapper
                    rightSection={rightSection}
                    rightRef={rightRef}
                    isRightWrapped={isRightWrapped}
                    variant="employerContribution"
                />
            </div>
        </div>
    );
}

